import { AddCircle } from '@mui/icons-material';
import { Box, Button, Theme, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../lib/AlertContext';
import { OrganizationService } from '../../../services/organization/organization.service';
import { UserRole } from '../../../services/user/types';

import { GetUsersInOrganization } from '../../../services/organization/types';
import { OrganizationId } from '../../../types/idFlavors';
import { AddUserModal } from './AddUserModal';
import { UsersList } from './UsersList';

const styles = {
    container: { display: 'flex', justifyContent: 'space-between', margin: '24px', flexDirection: 'column' },
    usersListContainer: {
        flexDirection: 'row',
        display: 'flex',
        justifyContent: 'space-between',
    },
    buttonContainer: (theme: Theme): object => ({
        paddingLeft: theme.spacing(3),
    }),
    addUserButton: {
        marginBottom: '16px',
        textTransform: 'uppercase',
    },
};

type UserSectionProps = {
    organizationId: OrganizationId;
    usersInOrg: GetUsersInOrganization[];
    isUserOwner: boolean;
    setUsersInOrg: (users: GetUsersInOrganization[]) => void;
    isCredentialCreated: boolean;
};
export const UserSection: React.FC<UserSectionProps> = (props) => {
    const { organizationId, usersInOrg, isUserOwner, setUsersInOrg, isCredentialCreated } = props;
    const { t } = useTranslation();
    const { showErrorToUser } = useAlert();
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleError = (error?: any): void => {
        const errorCode = error?.errorCode;
        const isUserNotFound = errorCode === 'USER_NOT_FOUND';

        showErrorToUser({
            errorTitle: t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_ADD_USER_TO_ORG_TITLE'),
            errorDescription: isUserNotFound
                ? t('ORGANIZATION_DETAILS_PAGE.USER_NOT_FOUND_ERROR')
                : t('ORGANIZATION_DETAILS_PAGE.FAILED_TO_ADD_USER_TO_ORG_BODY'),
            confirmText: t('COMMON.TRY_AGAIN'),
            cancelText: t('COMMON.CANCEL'),
            onConfirm: () => {
                setShowAddUserModal(true);
            },
            onCancel: () => {
                setShowAddUserModal(false);
            },
        });
    };

    const updateUsersList = async (): Promise<void> => {
        const updatedUsers = await OrganizationService.getUsersInOrganization(organizationId);
        setUsersInOrg(updatedUsers);
    };

    const handleAddUserToOrg = async (userEmail: string, role: UserRole): Promise<void> => {
        try {
            setLoading(true);
            const body = {
                organizationId: organizationId,
                email: userEmail,
                role,
            };
            await OrganizationService.assignOrganizationRoleToUser(body);
            await updateUsersList();
            setLoading(false);
            setShowAddUserModal(false);
        } catch (e) {
            setLoading(false);
            setShowAddUserModal(false);
            handleError(e);
        }
    };

    return (
        <Box sx={styles.container}>
            <Box sx={styles.usersListContainer}>
                <Typography variant="h6">{t('ORGANIZATION_DETAILS_PAGE.USERS_ASSIGNED_TO_ORG_TITLE')}</Typography>
                {isUserOwner && (
                    <Box sx={styles.buttonContainer}>
                        <Button
                            startIcon={<AddCircle />}
                            sx={styles.addUserButton}
                            variant="outlined"
                            color="primary"
                            disableElevation
                            disabled={isCredentialCreated}
                            data-testid="add-user-button"
                            onClick={(): void => {
                                setShowAddUserModal(true);
                            }}
                        >
                            {t('ORGANIZATION_DETAILS_PAGE.ADD_USER')}
                        </Button>
                    </Box>
                )}
            </Box>
            <UsersList
                users={usersInOrg}
                setUsersInOrg={setUsersInOrg}
                organizationId={organizationId}
                isUserOwner={isUserOwner}
                isCredentialCreated={isCredentialCreated}
            />
            {showAddUserModal && (
                <AddUserModal
                    handleAddUserToOrg={handleAddUserToOrg}
                    setShowAddUserModal={setShowAddUserModal}
                    loading={loading}
                />
            )}
        </Box>
    );
};

export default UserSection;
